import { useCallback, useMemo, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { SUPPORTED_ACTIONS } from '../actionsConfig';
import { SoarQueryKeys } from '../queryKeys';
import { ActionCatalogDto } from '../services/catalogService';
import { PlaybookCreateDto, PlaybookDto, playbookService } from '../services/playbookService';
import { Playbook } from '../types';

const STORAGE_KEY = 'soar_new_playbook_editing';

export interface PlaybookAction {
  id: string;
  type?: string;
  inputs: Record<string, any>;
}

export interface NewPlaybookState {
  editingPlaybookId: string | null;
  name: string;
  description: string;
  entityType?: string;
  isActive: boolean;
  usersAllowedToEdit: string[];
  usersAllowedToTrigger: string[];
  manualTriggeringAllowed: boolean;
  allowedInApps: string[];
  actions: PlaybookAction[];
  editable: boolean;
  triggerable: boolean;
  isLoading: boolean;
  isFetchingDetails: boolean;
}

export const useNewPlaybook = (
  onPlaybookSaved?: (playbook: Playbook, isNew: boolean) => void,
  catalogActions: ActionCatalogDto[] = [],
  existingPlaybooks: Playbook[] = []
) => {
  const queryClient = useQueryClient();
  const initialState: NewPlaybookState = {
    editingPlaybookId: null,
    name: '',
    description: '',
    entityType: undefined,
    isActive: true,
    usersAllowedToEdit: [],
    usersAllowedToTrigger: [],
    manualTriggeringAllowed: true,
    allowedInApps: [],
    actions: [],
    editable: true,
    triggerable: true,
    isLoading: false,
    isFetchingDetails: false,
  };

  const [state, setState] = useState<NewPlaybookState>(() => {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved) {
      try {
        return { ...initialState, ...JSON.parse(saved) };
      } catch (e) {
        console.error('Failed to load new playbook state', e);
      }
    }
    return initialState;
  });

  const persist = useCallback((newState: Partial<NewPlaybookState>) => {
    setState(prev => {
      const updated = { ...prev, ...newState };
      const toPersist = {
        editingPlaybookId: updated.editingPlaybookId,
        name: updated.name,
        description: updated.description,
        entityType: updated.entityType,
        isActive: updated.isActive,
        usersAllowedToEdit: updated.usersAllowedToEdit,
        usersAllowedToTrigger: updated.usersAllowedToTrigger,
        manualTriggeringAllowed: updated.manualTriggeringAllowed,
        allowedInApps: updated.allowedInApps,
        actions: updated.actions,
        editable: updated.editable,
        triggerable: updated.triggerable,
      };
      localStorage.setItem(STORAGE_KEY, JSON.stringify(toPersist));
      return updated;
    });
  }, []);

  const setName = useCallback((name: string) => persist({ name }), [persist]);
  const setDescription = useCallback((description: string) => persist({ description }), [persist]);
  const setEntityType = useCallback((entityType: string) => persist({ entityType }), [persist]);
  const setIsActive = useCallback((isActive: boolean) => persist({ isActive }), [persist]);
  const setUsersAllowedToEdit = useCallback((users: string[]) => persist({ usersAllowedToEdit: users }), [persist]);
  const setUsersAllowedToTrigger = useCallback((users: string[]) => persist({ usersAllowedToTrigger: users }), [persist]);
  const setManualTriggeringAllowed = useCallback((allowed: boolean) => persist({ manualTriggeringAllowed: allowed }), [persist]);
  const setAllowedInApps = useCallback((apps: string[]) => persist({ allowedInApps: apps }), [persist]);

  const addAction = useCallback(() => {
    const id = `temp_${Math.random().toString(36).substr(2, 9)}`;
    persist({ actions: [...state.actions, { id, type: undefined, inputs: {} }] });
    return id;
  }, [state.actions, persist]);

  const removeAction = useCallback(
    (id: string) => {
      persist({ actions: state.actions.filter(a => a.id !== id) });
    },
    [state.actions, persist]
  );

  const updateActionType = useCallback(
    (id: string, type: string) => {
      persist({
        actions: state.actions.map(action => {
          if (action.id === id) {
            const definition = SUPPORTED_ACTIONS.find(d => d.type === type);
            const newInputs = definition
              ? definition.fields.reduce(
                  (acc, f) => {
                    if (f.defaultValue !== undefined) {
                      return { ...acc, [f.name]: f.defaultValue };
                    }
                    if (f.type === 'fields-list' || f.type === 'potential-actions-list') {
                      return { ...acc, [f.name]: [] };
                    }
                    if (f.type === 'number') {
                      return { ...acc, [f.name]: 0 };
                    }
                    return { ...acc, [f.name]: '' };
                  },
                  {} as Record<string, any>
                )
              : {};
            return { ...action, type, inputs: newInputs };
          }
          return action;
        }),
      });
    },
    [state.actions, persist]
  );

  const updateActionInput = useCallback(
    (id: string, fieldName: string, value: any) => {
      persist({
        actions: state.actions.map(action => (action.id === id ? { ...action, inputs: { ...action.inputs, [fieldName]: value } } : action)),
      });
    },
    [state.actions, persist]
  );

  const resetForm = useCallback(() => {
    persist({
      editingPlaybookId: null,
      name: '',
      description: '',
      entityType: undefined,
      isActive: true,
      usersAllowedToEdit: [],
      usersAllowedToTrigger: [],
      manualTriggeringAllowed: true,
      allowedInApps: [],
      actions: [],
      editable: true,
      triggerable: true,
    });
  }, [persist]);

  const loadPlaybook = useCallback(
    async (playbook: Playbook) => {
      setState(prev => ({ ...prev, isFetchingDetails: true }));

      const initialActions = (playbook.actions || []).map(actionDto => ({
        id: actionDto.actionId || Math.random().toString(36).substr(2, 9),
        type: actionDto.actionType || undefined,
        inputs: actionDto.config || {},
      }));

      persist({
        editingPlaybookId: playbook.id,
        name: playbook.name || '',
        description: playbook.description || '',
        entityType: playbook.entityType || undefined,
        isActive: playbook.status === 'Active',
        usersAllowedToEdit: [], // Default until full details loaded
        usersAllowedToTrigger: [],
        manualTriggeringAllowed: true,
        allowedInApps: [],
        actions: initialActions,
        editable: playbook.editable !== false,
        triggerable: playbook.triggerable !== false,
      });

      try {
        const fullPlaybook = await playbookService.getPlaybookById(playbook.id);
        const fullActions = (fullPlaybook.actions || []).map(actionDto => ({
          id: actionDto.actionId || Math.random().toString(36).substr(2, 9),
          type: actionDto.actionType || undefined,
          inputs: actionDto.config || {},
        }));

        persist({
          name: fullPlaybook.name || playbook.name,
          description: fullPlaybook.description || playbook.description,
          entityType: fullPlaybook.entityType || playbook.entityType || undefined,
          isActive: fullPlaybook.enabled,
          usersAllowedToEdit: fullPlaybook.usersAllowedToEdit || [],
          usersAllowedToTrigger: fullPlaybook.usersAllowedToTrigger || [],
          manualTriggeringAllowed: fullPlaybook.manualTriggeringAllowed ?? true,
          allowedInApps: fullPlaybook.allowedInApps || [],
          actions: fullActions,
          editable: fullPlaybook.editable !== false,
          triggerable: fullPlaybook.triggerable !== false,
          isFetchingDetails: false,
        });
      } catch (error) {
        console.error('Failed to fetch playbook details', error);
        setState(prev => ({ ...prev, isFetchingDetails: false }));
      }
    },
    [persist]
  );

  const nameError = useMemo(() => {
    const trimmed = state.name?.trim().toLowerCase();
    if (!trimmed) return '';
    const duplicate = existingPlaybooks.find(p => p.name?.trim().toLowerCase() === trimmed && p.id !== state.editingPlaybookId);
    return duplicate ? 'A playbook with this name already exists' : '';
  }, [state.name, state.editingPlaybookId, existingPlaybooks]);

  const isValid = useMemo(
    () =>
      (state.name?.trim()?.length || 0) > 0 &&
      !nameError &&
      (state.entityType?.trim()?.length || 0) > 0 &&
      state.actions.length > 0 &&
      state.actions.every(a => {
        if (!a.type) return false;
        const definition = SUPPORTED_ACTIONS.find(d => d.type === a.type);
        if (!definition) return true;
        return definition.fields
          .filter(f => f.isRequired && f.isConfigurable)
          .every(f => {
            const val = a.inputs[f.name];
            if (val === undefined || val === null || val === '') return false;
            if (Array.isArray(val) && val.length === 0) return false;
            return true;
          });
      }),
    [state.name, nameError, state.entityType, state.actions]
  );

  const saveMutation = useMutation({
    mutationFn: async () => {
      if (!isValid || !state.entityType) return null;

      const actions = state.actions.map((a, index) => {
        const definition = SUPPORTED_ACTIONS.find(d => d.type === a.type);

        // Filter out empty strings, null, or undefined from config
        const filteredConfig = Object.entries(a.inputs).reduce(
          (acc, [key, value]) => {
            if (value !== '' && value !== null && value !== undefined) {
              return { ...acc, [key]: value };
            }
            return acc;
          },
          {} as Record<string, any>
        );

        return {
          actionId: a.id && a.id.startsWith('temp_') ? undefined : a.id,
          name: definition?.label || a.type!,
          actionType: a.type!,
          config: filteredConfig,
          orderIndex: index,
          enabled: true,
        };
      });

      const isNew = !state.editingPlaybookId;
      const savedPlaybook: Playbook = await (async () => {
        if (state.editingPlaybookId) {
          const playbookData: Partial<PlaybookDto> = {
            playbookId: state.editingPlaybookId,
            name: state.name,
            description: state.description,
            entityType: state.entityType!,
            enabled: state.isActive,
            usersAllowedToEdit: state.usersAllowedToEdit,
            usersAllowedToTrigger: state.usersAllowedToTrigger,
            manualTriggeringAllowed: state.manualTriggeringAllowed,
            allowedInApps: state.allowedInApps,
            actions,
          };

          const updatedDto = await playbookService.updatePlaybook(state.editingPlaybookId, playbookData);

          return {
            id: state.editingPlaybookId!,
            name: updatedDto.name || state.name,
            description: updatedDto.description || state.description,
            entityType: updatedDto.entityType || state.entityType!,
            status: updatedDto.enabled ? 'Active' : 'Inactive',
            lastUpdate: updatedDto.updatedAt || new Date().toISOString(),
            owner: 'System',
            runs: 0,
            successFailed: '0/0',
            actions: updatedDto.actions,
          };
        }
        const playbookData: PlaybookCreateDto = {
          playbookName: state.name,
          playbookDescription: state.description,
          entityType: state.entityType!,
          enabled: state.isActive,
          usersAllowedToEdit: state.usersAllowedToEdit,
          usersAllowedToTrigger: state.usersAllowedToTrigger,
          allowedInApps: state.allowedInApps,
          manualTriggeringAllowed: true,
          actions,
        };

        const createdDto = await playbookService.createPlaybook(playbookData);

        return {
          id: createdDto.playbookId,
          name: createdDto.name || state.name,
          description: createdDto.description || state.description,
          entityType: createdDto.entityType || state.entityType!,
          status: createdDto.enabled ? 'Active' : 'Inactive',
          owner: createdDto.createdByUserId || 'System',
          lastUpdate: new Date().toISOString(),
          runs: 0,
          successFailed: '0/0',
          actions: createdDto.actions,
        };
      })();

      return { savedPlaybook, isNew };
    },
    onSuccess: data => {
      if (data && onPlaybookSaved) {
        onPlaybookSaved(data.savedPlaybook, data.isNew);
      }
      queryClient.invalidateQueries({ queryKey: [SoarQueryKeys.PLAYBOOKS] });
    },
  });

  const actionOptions = useMemo(() => {
    const getBaseOptions = () => {
      if (state.entityType && catalogActions.length > 0) {
        const entityActions = catalogActions.filter(a => a.entityType === state.entityType);
        if (entityActions.length > 0) {
          return entityActions.map(a => ({ title: a.actionName || a.actionType, value: a.actionType }));
        }
      }
      return [];
    };

    return (getBaseOptions() || []).sort((a, b) => a.title.localeCompare(b.title));
  }, [state.entityType, catalogActions]);

  return {
    ...state,
    isValid,
    nameError,
    actionOptions,
    isSaving: saveMutation.isPending,
    setName,
    setDescription,
    setEntityType,
    setIsActive,
    setUsersAllowedToEdit,
    setUsersAllowedToTrigger,
    setManualTriggeringAllowed,
    setAllowedInApps,
    addAction,
    removeAction,
    updateActionType,
    updateActionInput,
    savePlaybook: saveMutation.mutateAsync,
    loadPlaybook,
    resetForm,
  };
};
